import { MultiSelect, SimpleSelect } from 'corso-admin-ui';
import { Pegasus, ShipmentMethod, shipmentMethodName } from 'corso-types';
import { Controller, useWatch } from 'react-hook-form';
import { useIntegrationSettingsData } from '~/hooks/useIntegrationSettings';
import { useReturnLocations } from '~/hooks/useReturnLocations';
import { useAutomationFormContext } from '../hooks/useAutomationForm';

const returnHandlingLabels = {
  [Pegasus.ReturnHandling.returnLocation]: 'Return to a Location',
  [Pegasus.ReturnHandling.globalE]: 'Managed by Global-e',
} as const satisfies Record<Pegasus.ReturnHandling, string>;

const returnHandlingDetails = {
  [Pegasus.ReturnHandling.returnLocation]:
    'Items come back to one of your Return Locations using the methods you offer.',
  [Pegasus.ReturnHandling.globalE]:
    'Global-e decides the destination and issues the label. Use the conditions above to pick the countries this applies to.',
} as const satisfies Record<Pegasus.ReturnHandling, string>;

export default function EventReturnShippingConfig() {
  const { control, setValue } = useAutomationFormContext();

  const { data: returnLocations } = useReturnLocations();
  const { data: { globalEConfig } = {} } = useIntegrationSettingsData();

  // useWatch, not watch: `setValue('event.params', ...)` doesn't notify a watcher on a child path
  const returnHandling =
    useWatch({ control, name: 'event.params.returnHandling' }) ??
    Pegasus.ReturnHandling.returnLocation;

  // Global-e owns its own locations; pointing a merchant policy at one would quote
  // carrier labels to an address Global-e controls
  const returnLocationOptions = (returnLocations ?? [])
    .filter((rl) => !rl.isGlobalEManaged)
    .map((rl) => ({ value: `${rl.id}`, label: rl.name }));

  //? a consolidated dropoff is not required to configure
  const returnMethodOptions = [
    ShipmentMethod.label,
    ShipmentMethod.packingSlip,
  ].map((type) => ({
    value: type,
    label: shipmentMethodName[type],
  }));

  const returnHandlingOptions = [
    Pegasus.ReturnHandling.returnLocation,
    ...(globalEConfig ? [Pegasus.ReturnHandling.globalE] : []),
  ].map((value) => ({ value, label: returnHandlingLabels[value] }));

  return (
    <>
      {returnHandlingOptions.length > 1 && (
        <SimpleSelect
          label="Return Handling"
          options={returnHandlingOptions}
          value={returnHandling}
          onChange={(value) =>
            setValue(
              'event.params',
              value === Pegasus.ReturnHandling.globalE
                ? { returnHandling: value }
                : {
                    returnHandling: Pegasus.ReturnHandling.returnLocation,
                    returnLocationId: 0,
                    returnMethods: [],
                  },
              { shouldDirty: true },
            )
          }
          details={returnHandlingDetails[returnHandling]}
        />
      )}

      {returnHandling === Pegasus.ReturnHandling.returnLocation && (
        <>
          <Controller
            control={control}
            name="event.params.returnLocationId"
            render={({ field: { onChange, value }, fieldState }) => (
              <SimpleSelect
                options={returnLocationOptions}
                label="Return Location"
                value={`${value}`}
                onChange={onChange}
                error={fieldState.error?.message}
                details="The location items will be returned to."
              />
            )}
          />

          <Controller
            control={control}
            name="event.params.returnMethods"
            render={({ field, fieldState }) => (
              <MultiSelect
                label="Return Methods"
                details="Methods to offer customers for returning items."
                placeholder="Select Methods"
                options={returnMethodOptions}
                value={field.value.map((value) => ({
                  label: shipmentMethodName[value],
                  value,
                }))}
                onChange={(selected) =>
                  field.onChange(selected.map(({ value }) => value))
                }
                error={fieldState.error?.message}
              />
            )}
          />
        </>
      )}
    </>
  );
}
